import { useAuthStore } from "@/states";

export type Role = "owner" | "admin" | "sales" | "operations" | "accounts" | "guest";
export type ExtendedRole = Role | "super_admin";

export const hasAnyRole = (role: string | undefined, allowed: ExtendedRole[]) => {
  if (!role) return false;
  return allowed.includes(role as ExtendedRole);
};

// Lead & Itinerary permissions
export const canEditOrCreateLead = (role?: string) => hasAnyRole(role, ["owner", "admin", "sales", "operations"]);
/** Determines whether a role can access private lead and customer documents. */
export const canManageDocuments = (role?: string) => canEditOrCreateLead(role);
export const canEditOrCreateItinerary = (role?: string) => hasAnyRole(role, ["owner", "admin", "sales", "operations"]);
/** Allows every authenticated CRM role to import a new itinerary without granting existing-itinerary editing. */
export const canImportItinerary = (role?: string) =>
  hasAnyRole(role, ["super_admin", "owner", "admin", "sales", "operations", "accounts", "guest"]);
/** Allows import-only roles to edit/save an approved new draft, never an existing itinerary. */
export const canEditItineraryDraft = (role?: string, itineraryId?: string, importedDraft = false) =>
  canEditOrCreateItinerary(role) || (canImportItinerary(role) && !itineraryId && importedDraft);

/** Determines whether a CRM role may configure tenant mailboxes. */
export const canManageMailboxes = (role?: string) => hasAnyRole(role, ["super_admin", "owner", "admin"]);

// Organisation & settings
export const canAccessOrg = (role?: string) => hasAnyRole(role, ["owner", "admin", "sales", "operations", "accounts"]);
export const canManageTeams = (role?: string) => hasAnyRole(role, ["super_admin", "owner", "admin"]);
export const canManageUsers = (role?: string) => hasAnyRole(role, ["super_admin", "owner", "admin"]);
export const canManageOwnerUsers = (role?: string) => hasAnyRole(role, ["super_admin", "owner"]);
export const canDeleteOwnerUsers = (role?: string) => hasAnyRole(role, ["super_admin"]);
export const canManageContacts = (role?: string) => hasAnyRole(role, ["owner", "admin"]);
export const canAccessCampaigns = (role?: string) =>
  hasAnyRole(role, ["owner", "admin", "sales", "operations", "accounts", "guest"]);
export const canManageCampaigns = (role?: string) => hasAnyRole(role, ["owner", "admin"]);
export const canManageOrgSettings = (role?: string) =>
  hasAnyRole(role, ["super_admin", "owner", "admin", "sales", "operations"]);
/** Matches the tenant-image upload and delete permissions enforced by the API. */
export const canManageTenantImage = (role?: string) => hasAnyRole(role, ["super_admin", "owner", "admin"]);
/** Determines whether a tenant user can edit organisation-wide company document details. */
export const canManageCompanyProfile = (role?: string) => hasAnyRole(role, ["super_admin", "owner", "admin"]);
/** Determines whether the role can manage tenant-owned quote currency conversion rates. */
export const canManageCurrencyConversions = (role?: string) => hasAnyRole(role, ["owner", "admin", "super_admin"]);
/** Determines whether the role can manage tenant nationality groups used for nationality-specific rates. */
export const canManageNationalityGroups = (role?: string) => hasAnyRole(role, ["owner", "admin", "super_admin"]);
/** Allows super admins always, and tenant administrators only when enabled for their tenant. */
export const canManageAISettings = (role?: string, showAISettings = false) =>
  role === "super_admin" || (showAISettings && hasAnyRole(role, ["owner", "admin"]));
export const canManageAutomatedEmails = (role?: string) => hasAnyRole(role, ["super_admin", "owner", "admin"]);
/** Determines whether a role can add or reconnect tenant-owned WhatsApp accounts. */
export const canManageWhatsAppAccounts = (role?: string) => hasAnyRole(role, ["super_admin", "owner", "admin"]);
/** Determines whether a role can reply to the tenant's WhatsApp conversations. */
export const canSendWhatsAppMessages = (role?: string) =>
  hasAnyRole(role, ["super_admin", "owner", "admin", "sales", "operations"]);
/** Determines whether a role can configure the tenant's agent portal and pricing profile. */
export const canManageAgentPortal = (role?: string) => hasAnyRole(role, ["super_admin", "owner", "admin"]);
export const canUpdateTenantUserLimit = (role?: string) => hasAnyRole(role, ["super_admin"]);
export const canViewTeams = (role?: string) => hasAnyRole(role, ["super_admin", "owner", "admin"]);
export const canViewUsers = (role?: string) => hasAnyRole(role, ["super_admin", "owner", "admin"]);
/** Determines whether a role may export leads. Admin exports have guest phone numbers and emails masked by the API. */
export const canExportLeads = (role?: string) => hasAnyRole(role, ["super_admin", "owner", "admin"]);
/** Determines whether a role receives unmasked guest phone numbers and emails in lead exports. */
export const canExportFullLeadContacts = (role?: string) => hasAnyRole(role, ["super_admin", "owner"]);
/** Determines whether the actor may export tenant itineraries. */
export const canExportItineraries = (role?: string) => hasAnyRole(role, ["super_admin", "owner"]);
export const canExportPayments = (role?: string) => hasAnyRole(role, ["super_admin", "owner"]);
export const canExportHotels = (role?: string) => hasAnyRole(role, ["super_admin", "owner"]);
export const canImportHotels = (role?: string) => hasAnyRole(role, ["super_admin", "owner", "admin"]);
export const canImportExportHotels = (role?: string) => hasAnyRole(role, ["super_admin", "owner", "admin"]);
export const canBulkEditLeads = (role?: string) => hasAnyRole(role, ["super_admin", "owner", "admin"]);

// Payments
export const canVerifyPayments = (role?: string) => hasAnyRole(role, ["super_admin", "owner", "admin", "accounts"]);
export const canDeleteUnverifiedPayment = (role?: string) =>
  hasAnyRole(role, ["super_admin", "owner", "admin", "accounts"]);
export const canMovePaymentSchedule = (role?: string) =>
  hasAnyRole(role, ["super_admin", "owner", "admin", "accounts"]);
/** Determines whether a user can amend a verified payment. */
export const canAmendVerifiedPayment = (role?: string, hasAccess?: boolean, accessExpiresAt?: Date | string | null) =>
  role === "owner" || Boolean(hasAccess && (!accessExpiresAt || new Date(accessExpiresAt).getTime() > Date.now()));
/**
 * Determines if the role can access the admin dashboard
 * @param role Current user role
 */
export const canAccessDashboard = (role?: string) => hasAnyRole(role, ["owner", "admin"]);
/**
 * Determines if the role can access the user (personal performance) dashboard
 * @param role Current user role
 */
export const canAccessUserDashboard = (role?: string) => hasAnyRole(role, ["sales", "operations", "accounts"]);
/** Determines whether a role can access tenant-scoped or personal reporting. */
export const canAccessReports = (role?: string) =>
  hasAnyRole(role, ["owner", "admin", "sales", "operations", "accounts"]);
/** Determines access to organisation-wide tax accounting and reconciliation. */
export const canAccessTaxation = (role?: string) => hasAnyRole(role, ["owner", "admin", "accounts"]);
/** Determines whether a role can view organisation-wide report data and manage shared reports. */
export const canManageSharedReports = (role?: string) => hasAnyRole(role, ["owner", "admin"]);
/**
 * Determines if the role can access the super-admin panel
 * @param role Current user role
 */
export const canAccessAdminPanel = (role?: string) => hasAnyRole(role, ["super_admin"]);

// Hook helper: get current user role
/**
 * Returns permission flags for the current user
 */
export const usePermissions = () => {
  const user = useAuthStore((s) => s.user);
  const role = user?.role;
  return {
    role,
    canAccessDashboard: canAccessDashboard(role),
    canAccessAdminPanel: canAccessAdminPanel(role),
    canAccessOrg: canAccessOrg(role),
    canEditOrCreateLead: canEditOrCreateLead(role),
    canManageDocuments: canManageDocuments(role),
    canEditOrCreateItinerary: canEditOrCreateItinerary(role),
    canImportItinerary: canImportItinerary(role),
    canManageTeams: canManageTeams(role),
    canManageUsers: canManageUsers(role),
    canManageOwnerUsers: canManageOwnerUsers(role),
    canDeleteOwnerUsers: canDeleteOwnerUsers(role),
    canManageContacts: canManageContacts(role),
    canAccessCampaigns: canAccessCampaigns(role),
    canManageCampaigns: canManageCampaigns(role),
    canManageOrgSettings: canManageOrgSettings(role),
    canManageCompanyProfile: canManageCompanyProfile(role),
    canManageCurrencyConversions: canManageCurrencyConversions(role),
    canManageNationalityGroups: canManageNationalityGroups(role),
    canUpdateTenantUserLimit: canUpdateTenantUserLimit(role),
    canViewTeams: canViewTeams(role),
    canViewUsers: canViewUsers(role),
    canExportLeads: canExportLeads(role),
    canExportFullLeadContacts: canExportFullLeadContacts(role),
    canExportPayments: canExportPayments(role),
    canExportHotels: canExportHotels(role),
    canImportHotels: canImportHotels(role),
    canImportExportHotels: canImportExportHotels(role),
    canBulkEditLeads: canBulkEditLeads(role),
    canVerifyPayments: canVerifyPayments(role),
    canDeleteUnverifiedPayment: canDeleteUnverifiedPayment(role),
    canMovePaymentSchedule: canMovePaymentSchedule(role),
    canAmendVerifiedPayment: canAmendVerifiedPayment(
      role,
      user?.canAmendVerifiedPayments,
      user?.paymentAmendAccessExpiresAt,
    ),
    canManageAISettings: canManageAISettings(role, user?.tenant?.showAISettings === true),
    canManageAutomatedEmails: canManageAutomatedEmails(role),
    canManageWhatsAppAccounts: canManageWhatsAppAccounts(role),
    canSendWhatsAppMessages: canSendWhatsAppMessages(role),
    canManageAgentPortal: canManageAgentPortal(role),
    canAccessUserDashboard: canAccessUserDashboard(role),
    canAccessReports: canAccessReports(role),
    canManageSharedReports: canManageSharedReports(role),
  };
};
